import { motion } from "framer-motion";
import { ArrowRight, Info } from "lucide-react";
import { PRICING_GROUPS, ADDITIONAL_SERVICES, CONTACT } from "@/data/site";

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="rs-section bg-white"
    >
      <div className="rs-container">
        <div className="max-w-3xl">
          <p className="rs-overline mb-4">Transparent Pricing</p>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
            Honest prices.{" "}
            <span className="text-[#F97316]">No surprises.</span>
          </h2>
          <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed">
            Our most-requested services are flat-priced below. For everything else, send a
            photo and we&apos;ll quote it on the spot — no obligation.
          </p>
        </div>

        {/* Flat-priced service groups */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          {PRICING_GROUPS.map((group, idx) => (
            <PricingCard key={group.title} group={group} idx={idx} />
          ))}
        </div>

        {/* Additional services (quote-based) */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mt-10 rounded-2xl bg-[#F8FAFC] border border-slate-200 p-7 md:p-9"
          data-testid="additional-services"
        >
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#F97316]">
                Additional Services
              </div>
              <h3 className="mt-2 font-display font-semibold text-xl md:text-2xl text-[#0A192F]">
                Quoted on request
              </h3>
            </div>
            <a href="#contact" className="rs-btn-primary text-sm">
              Request a Quote
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {ADDITIONAL_SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  className="flex items-center justify-between gap-3 rounded-xl bg-white border border-slate-200 px-4 py-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-[#F97316]/10 grid place-items-center">
                      <Icon strokeWidth={1.7} className="h-4 w-4 text-[#F97316]" />
                    </div>
                    <div className="text-sm font-semibold text-[#0A192F]">{s.title}</div>
                  </div>
                  <div className="text-xs font-semibold text-slate-500">{s.note}</div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Materials note */}
        <div className="mt-6 flex items-start gap-3 rounded-xl bg-[#0A192F] text-white p-4 md:p-5">
          <Info className="h-5 w-5 text-[#F97316] flex-shrink-0 mt-0.5" />
          <p className="text-sm md:text-base text-white/85 leading-relaxed">
            <span className="font-semibold">Materials &amp; specialized hardware may cost extra.</span>{" "}
            Standard labor is included in the posted prices. Specialty mounts, in-wall cable kits,
            heavy-duty anchors, and non-drywall surfaces (brick, tile, stone, concrete) are quoted
            up front before work begins.
          </p>
        </div>

        <div className="mt-10 text-center">
          <a href={`tel:${CONTACT.phoneRaw}`} className="rs-btn-secondary">
            Prefer to call? {CONTACT.phone}
          </a>
        </div>
      </div>
    </section>
  );
}

function PricingCard({ group, idx }) {
  const Icon = group.icon;
  return (
    <motion.div
      data-testid={`pricing-group-${idx}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (idx % 2) * 0.08 }}
      className="rounded-2xl border border-slate-200 bg-white p-7 hover:border-[#F97316]/40 hover:shadow-[0_18px_40px_-18px_rgba(10,25,47,0.18)] transition-all"
    >
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-[#F97316]/10 grid place-items-center">
          <Icon strokeWidth={1.7} className="h-5 w-5 text-[#F97316]" />
        </div>
        <div>
          <h3 className="font-display font-semibold text-xl text-[#0A192F] leading-tight">
            {group.title}
          </h3>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mt-0.5">
            {group.subtitle}
          </div>
        </div>
      </div>

      <ul className="mt-6 divide-y divide-slate-100">
        {group.items.map((it) => {
          const custom = it.price === "Custom Quote";
          return (
            <li
              key={it.label}
              className="flex items-baseline justify-between gap-4 py-3"
              data-testid={`pricing-row-${group.title.toLowerCase().replace(/\s+/g, "-")}-${it.label}`}
            >
              <span className="text-slate-700 text-base">{it.label}</span>
              {custom ? (
                <span className="font-display font-semibold text-base text-slate-500">
                  {it.price}
                </span>
              ) : (
                <span className="font-display font-bold text-3xl md:text-4xl text-[#F97316] leading-none">
                  {it.price}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </motion.div>
  );
}
